import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useStore } from "../lib/storeContext";
import { AssetImage } from "../components/AssetImage";
import { Lightbox } from "../components/Lightbox";
import type { LightboxSlide } from "../components/Lightbox";
import { Breadcrumbs, EmptyState, SectionTitle, Spinner } from "./ui";
import { RenameButton } from "./RenameButton";
import { DeleteButton } from "./DeleteButton";
import { pluralize, formatDateTime } from "../lib/util";
import { createSplatFromAssets } from "../lib/apiClient";
import { useJob } from "../lib/useJob";
import { cycleNeedsJobFallback, cycleRenderCount } from "../lib/concepts";
import type { StoredEntity } from "../lib/entityStore";
import type { CyclePayload, PhotoPayload, RoomPayload, ScanPayload, SessionPayload } from "../lib/types";

export function RoomDetailView() {
  const { roomId = "" } = useParams();
  const navigate = useNavigate();
  const { store, rev, loading } = useStore();
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [creatingModel, setCreatingModel] = useState(false);
  const [createModelMsg, setCreateModelMsg] = useState<string | null>(null);
  void rev;

  if (loading) return <Spinner />;
  const room = store.get<RoomPayload>("ROOM", roomId);
  if (!room) return <EmptyState title="Room not found" icon="🔍" />;
  const project = room.parentId ? store.get("PROJECT", room.parentId) : undefined;
  const projectId = store.projectIdOf("ROOM", roomId);
  // Viewers of a shared project are read-only: no makeovers, no model creation.
  // Editors keep MAKEOVERS (the backend resolveJobDataOwner path routes render
  // results into the owner's partition). Splat creation is OWNER-ONLY, though:
  // POST /v1/splats/from-assets filters photo keys to the caller's own
  // users/<caller>/ prefix, so a shared room's owner-owned photo keys resolve to
  // 0 valid keys → 400. (from-assets was never extended to editors.) Owner = no
  // membership row for the room's project.
  const isViewer = store.roleFor(projectId) === "viewer";
  const isOwner = !store.membership(projectId ?? "");

  const photos = store.photos(roomId);
  const scans = store.scans(roomId);
  const sessions = store.sessions(roomId);
  const ideas = store.ideas(roomId);

  // W2: "Create photoreal model from photos" — gather the room's UNIQUE photo blob keys (the
  // pure-image trainer needs a coherent overlapping set; ~8 is the SfM floor, more is much better).
  const photoKeys = Array.from(
    new Set(
      photos.flatMap((p) =>
        (p.assets ?? []).filter((a) => a.kind === "photo" && a.s3Key).map((a) => a.s3Key),
      ),
    ),
  );

  const slides: LightboxSlide[] = photos.map((p) => ({
    s3Key: p.assets[0]?.s3Key ?? "",
    caption: room.payload.name,
  }));

  async function handleCreateFromPhotos() {
    if (creatingModel || !isOwner || photoKeys.length < 8) return;
    setCreatingModel(true);
    setCreateModelMsg(null);
    try {
      const r = await createSplatFromAssets(photoKeys);
      setCreateModelMsg(`Building a photoreal model from ${r.imageCount} photos — it'll appear under Models.`);
      navigate(`/splats/${r.splatId}/splat`);
    } catch (e) {
      setCreateModelMsg(e instanceof Error ? e.message : "Couldn't start the model.");
    } finally {
      setCreatingModel(false);
    }
  }

  return (
    <div className="space-y-7">
      <Breadcrumbs
        items={[
          { label: "Projects", to: "/" },
          { label: (project?.payload as RoomPayload | undefined)?.name ?? "Project", to: project ? `/projects/${project.id}` : undefined },
          { label: room.payload.name },
        ]}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold text-ink">{room.payload.name}</h1>
          <RenameButton type="ROOM" id={roomId} current={room.payload.name} />
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/rooms/${roomId}/ideabook`} className="btn-ghost text-sm">
            ♥ Ideabook{ideas.length > 0 ? ` (${ideas.length})` : ""}
          </Link>
          {!isViewer && (
            <button
              className="btn-warm text-sm disabled:opacity-50"
              disabled={photos.length === 0}
              onClick={() => navigate(`/rooms/${roomId}/makeover`)}
            >
              ✨ Start Redecorating
            </button>
          )}
        </div>
      </div>

      {/* Photos */}
      <section>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SectionTitle>{pluralize(photos.length, "Photo")}</SectionTitle>
          {photoKeys.length > 0 && isOwner && (
            <button
              className="btn-ghost text-sm disabled:opacity-50"
              disabled={creatingModel || photoKeys.length < 8}
              title={photoKeys.length < 8 ? "Need at least 8 photos for a photoreal model" : "Build a 3D photoreal model from these photos"}
              onClick={handleCreateFromPhotos}
            >
              {creatingModel ? "Starting…" : `✦ Create photoreal model from photos${photoKeys.length < 8 ? ` (need ${8 - photoKeys.length} more)` : ""}`}
            </button>
          )}
        </div>
        {createModelMsg && <p className="mt-1 text-xs text-muted">{createModelMsg}</p>}
        {photos.length === 0 ? (
          <EmptyState title="No photos yet" subtitle="Add photos in the app." icon="📷" />
        ) : (
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
            {photos.map((p, i) => {
              const isScan = !!(p.payload as PhotoPayload).scanID;
              return (
                <div key={p.id} className="relative">
                  <AssetImage
                    s3Key={p.assets[0]?.s3Key}
                    alt={`Photo ${i + 1}`}
                    className="aspect-square rounded-card shadow-card"
                    onClick={() => setLightbox(i)}
                  />
                  {isScan && (
                    <span className="absolute right-1.5 top-1.5 rounded-md bg-black/55 px-1.5 py-0.5 text-[10px] text-white">
                      ◳ scan
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* 3D scans */}
      {scans.length > 0 && (
        <section>
          <SectionTitle>3D scans</SectionTitle>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {/* Newest first + a LATEST badge, mirroring the iOS scan list (store.scans is ascending). */}
            {[...scans].reverse().map((s, i) => {
              const sp = s.payload as ScanPayload;
              const sqft = Math.round((sp.floorAreaSquareMeters || 0) * 10.7639104);
              const isLatest = i === 0;
              // Photoreal / training / keyframe / structure-only status — mirrors ScanRowView so an
              // empty relocalize re-scan no longer looks identical to a full photoreal-able capture.
              const training = sp.splatStatus === "queued" || sp.splatStatus === "training";
              const status = sp.splatModelFileName
                ? { label: "✦ Photoreal", cls: "text-accent" }
                : training
                  ? { label: "Training…", cls: "text-muted" }
                  : (sp.keyframeCount ?? 0) > 0
                    ? { label: `${sp.keyframeCount} frames`, cls: "text-muted" }
                    : { label: "structure only", cls: "text-muted/70" };
              return (
                <div key={s.id} className="card flex items-center gap-3 p-4 transition hover:shadow-lift">
                  <Link to={`/scans/${s.id}`} className="flex min-w-0 flex-1 items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-cream text-2xl">📐</div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-ink">Floorplan & 3D</span>
                        {isLatest && (
                          <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                            Latest
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted">
                        {sqft} sq ft · {pluralize(sp.wallCount, "wall")} · {formatDateTime(sp.createdAt)}
                      </div>
                      <div className={`text-xs ${status.cls}`}>{status.label}</div>
                    </div>
                  </Link>
                  <DeleteButton type="SCAN" id={s.id} what="scan" />
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Past makeovers */}
      {sessions.length > 0 && (
        <section>
          <SectionTitle>Past makeovers</SectionTitle>
          <div className="space-y-2">
            {sessions.map((s) => (
              <MakeoverRow key={s.id} session={s} roomId={roomId} />
            ))}
          </div>
        </section>
      )}

      {lightbox != null && (
        <Lightbox slides={slides} index={lightbox} onClose={() => setLightbox(null)} onIndexChange={setLightbox} />
      )}
    </div>
  );
}

// One makeover row. If a finished makeover's renders never synced from the phone
// (status "generating", empty cycle results), we recover the count + status from
// the still-completed backend job so it shows as done instead of "generating".
function MakeoverRow({ session, roomId }: { session: StoredEntity<SessionPayload>; roomId: string }) {
  const { store } = useStore();
  const sp = session.payload;
  const cycles = store.cycles(session.id);
  const latest = cycles[cycles.length - 1];
  const needsFallback = !!latest && cycleNeedsJobFallback(latest.payload as CyclePayload);
  const jobId = needsFallback ? (latest!.payload as CyclePayload).jobId ?? undefined : undefined;
  const { data: job, isLoading } = useJob(jobId, needsFallback);

  const ideaCount = cycles.reduce((n, c) => {
    const cp = c.payload as CyclePayload;
    return n + cycleRenderCount(cp, cp.jobId === jobId ? job : undefined);
  }, 0);

  let status = sp.status;
  if (sp.status !== "completed" && sp.status !== "failed" && job?.status === "completed") {
    status = "completed";
  }

  return (
    <Link
      to={`/rooms/${roomId}/sessions/${session.id}`}
      className="card flex items-center justify-between gap-3 p-4 transition hover:shadow-lift"
    >
      <div>
        <div className="font-medium text-ink">{sp.goal || "Makeover"}</div>
        <div className="text-xs text-muted">
          {formatDateTime(sp.createdAt)} · {isLoading && needsFallback ? "loading…" : pluralize(ideaCount, "idea")}
        </div>
      </div>
      <StatusBadge status={status} />
    </Link>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    completed: "bg-green-100 text-green-700",
    failed: "bg-red-100 text-red-700",
    processing: "bg-amber-100 text-amber-700",
    generating: "bg-amber-100 text-amber-700",
    pending: "bg-cream text-muted",
  };
  return (
    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${map[status] ?? "bg-cream text-muted"}`}>
      {status}
    </span>
  );
}
